import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { createApiClient } from '../lib/apiClient';
import { requestPasswordLink, sendEmailOtp, verifyEmailOtp } from '../lib/auth/authApi';
import './advisor.css';

type Step = 'email' | 'code' | 'passkey';

/**
 * Advisor sign-in.
 *
 * Returning advisor  → passkey, one touch.
 * First device       → email → 6-digit code → enroll a passkey.
 *
 * The code step is not a formality: the server will not let a passkey be
 * enrolled without an OTP-verified enrollment ticket proving the caller owns
 * the mailbox (S6c 1b). Same ceremony members use — no second auth system.
 */
export function SignInScreen() {
  const { signInWithPasskey, signInWithPassword, enrollPasskey } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [ticket, setTicket] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bare client: sign-in happens before there is a session to attach.
  const bare = createApiClient({ tokens: { get: () => null, set: () => {} } });

  async function handlePassword(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await signInWithPassword(email.trim(), password);
      navigate('/', { replace: true });
    } catch (e) {
      // The server says one thing for a wrong password and for no account, on
      // purpose — repeating it verbatim keeps that promise instead of guessing
      // a friendlier message that would give the difference away.
      setError(e instanceof Error && e.message !== '' ? e.message : 'We could not sign you in.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRequestLink(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await requestPasswordLink(bare, email.trim());
    } catch {
      // Deliberately ignored: the server answers the same either way, and a
      // failure message here would reveal what it refuses to.
    } finally {
      setLinkSent(true);
      setBusy(false);
    }
  }

  async function handlePasskey(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await signInWithPasskey(email.trim());
      navigate('/', { replace: true });
    } catch {
      // No credential on this device (or the user dismissed the prompt). Fall
      // through to the email-proof path rather than dead-ending them.
      setError('No passkey found for this device. Verify your email to add one.');
      setStep('code');
      try {
        await sendEmailOtp(bare, email.trim());
      } catch {
        setError('We could not send a code to that address.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSendCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await sendEmailOtp(bare, email.trim());
      setStep('code');
    } catch {
      setError('We could not send a code to that address.');
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await verifyEmailOtp(bare, email.trim(), code.trim());
      if (!res.verified || res.enrollmentTicket === undefined) {
        setError('That code was not right. Try again.');
        return;
      }
      setTicket(res.enrollmentTicket);
      setStep('passkey');
    } catch {
      setError('That code was not right. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleEnroll(): Promise<void> {
    if (ticket === null) return;
    setBusy(true);
    setError(null);
    try {
      await enrollPasskey(email.trim(), ticket);
      navigate('/', { replace: true });
    } catch (e) {
      // Surface the server's reason instead of a blanket "could not". The API
      // distinguishes "Sign in on this account before adding another device."
      // (this account already has a passkey — you want Continue, not enroll)
      // from "Verify your email before creating a passkey.", and swallowing
      // that difference turns a self-explanatory refusal into a dead end.
      const reason = e instanceof Error && e.message !== '' ? e.message : '';
      const friendly = /already has|another device|sign in on this account/i.test(reason)
        ? 'This account already has a passkey. Go back and choose Continue to sign in with it.'
        : reason !== ''
          ? reason
          : 'We could not add a passkey on this device.';
      setError(friendly);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth__panel la-card la-rise">
        <p className="la-label">Legacy Architect</p>
        <h1 className="auth__title">Advisor portal</h1>

        {step === 'email' && (
          <>
            <p className="auth__body">
              Your clients&apos; messages are sealed to them alone. You see the vault, never
              what&apos;s inside it.
            </p>
            <label className="auth__label" htmlFor="email">
              Work email
            </label>
            <input
              id="email"
              className="auth__input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label className="auth__label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="auth__input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                // A password field that ignores Enter feels broken.
                if (e.key === 'Enter' && !busy && email.trim() !== '' && password !== '') {
                  void handlePassword();
                }
              }}
            />
            <div className="la-cta-wrap">
              <button
                type="button"
                className="la-btn-primary"
                disabled={busy || email.trim() === '' || password === ''}
                onClick={() => void handlePassword()}
              >
                {busy ? 'One moment' : 'Sign in'}
              </button>
            </div>

            {/* The passkey is the shortcut on a device that already has one,
                not the way in. A firm works from whatever machine it is sitting
                at, and that is the case this screen has to serve first. */}
            <button
              type="button"
              className="auth__linkBtn"
              disabled={busy || email.trim() === ''}
              onClick={() => void handlePasskey()}
            >
              Use a passkey on this device
            </button>
            <button
              type="button"
              className="auth__linkBtn"
              disabled={busy || email.trim() === ''}
              onClick={() => void handleRequestLink()}
            >
              Set or reset your password
            </button>
            {linkSent && (
              <p className="auth__body auth__body--hint">
                If that address has an account, a link is on its way. It works once and
                expires in an hour.
              </p>
            )}
            <button
              type="button"
              className="auth__linkBtn"
              disabled={busy || email.trim() === ''}
              onClick={() => void handleSendCode()}
            >
              Use an email code instead
            </button>
          </>
        )}

        {step === 'code' && (
          <>
            <p className="auth__body">
              We sent a six-digit code to <strong>{email}</strong>.
            </p>
            <label className="auth__label" htmlFor="code">
              Verification code
            </label>
            <input
              id="code"
              className="auth__input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <div className="la-cta-wrap">
              <button
                type="button"
                className="la-btn-primary"
                disabled={busy || code.trim().length < 6}
                onClick={() => void handleVerifyCode()}
              >
                {busy ? 'Checking' : 'Verify'}
              </button>
            </div>
            <button
              type="button"
              className="auth__linkBtn"
              disabled={busy}
              onClick={() => void handleSendCode()}
            >
              Send another code
            </button>
          </>
        )}

        {step === 'passkey' && (
          <>
            <p className="auth__body">
              Add a passkey so you can sign in with your face, fingerprint, or device PIN. No
              password to lose.
            </p>
            <div className="la-cta-wrap">
              <button
                type="button"
                className="la-btn-primary"
                disabled={busy}
                onClick={() => void handleEnroll()}
              >
                {busy ? 'Adding' : 'Add passkey'}
              </button>
            </div>
          </>
        )}

        {error !== null && (
          <p className="auth__alert" role="alert">
            {error}
          </p>
        )}

        {/* The server refuses to add a passkey to an account that already has
            one unless you are signed in — correct, and the only thing standing
            between a leaked inbox and someone else's client list. But it left
            the person holding a refusal and no next step, on the device they
            are trying to start using.

            Say only what is TRUE: enrolPasskey is called from this screen and
            nowhere else in the app, so there is no "account settings" page to
            send them to and a second device is not currently possible at all.
            Pointing them at a screen that does not exist would be worse than
            the bare 403 — a confident wrong instruction costs more than a
            useless right one. */}
        {error !== null && /before adding another device/i.test(error) && (
          <p className="auth__body auth__body--hint">
            You already have a passkey on the device you set up first — please sign in there.
            Using a second device isn&rsquo;t something you can set up yourself yet, and if the
            first one is lost your Legacy Architect contact has to re-enable setup for you.
            That is deliberate: an email code alone must never be enough to reach your
            clients&rsquo; records.
          </p>
        )}
      </div>
    </div>
  );
}
